import { usePathname, useRouter } from 'next/navigation';

/**
 * Generates the news detail URL path based on the current pathname
 * @param newsId - The ID of the news item
 * @param pathname - Optional current pathname to extract version from
 * @param newsType - Optional news type to pass as query parameter
 * @returns The path to the news detail page
 */
export const getNewsDetailPath = (
  newsId: string, 
  pathname?: string | null,
  newsType?: string
): string => {
  const queryParams = new URLSearchParams();
  if (newsType) {
    queryParams.set("newsType", newsType);
  }

  const queryParam = queryParams.toString() ? `?${queryParams.toString()}` : "";
  
  if (!pathname) {
    // Default to web app route if pathname is not available
    return `/investments/news/${newsId}${queryParam}`;
  }

  // Check if we're in a mobile app route (has version prefix like app-v1 or app-v2)
  // Note: /in/ is not a version prefix for web app routes
  const versionMatch = /^\/(app-v1|app-v2)/.exec(pathname);
  
  if (versionMatch) {
    // Mobile app route with version
    const basePath = versionMatch[1];
    return `/${basePath}/investments/news/${newsId}${queryParam}`;
  }
  
  // Web app route - always return without /in/ prefix
  // Strip any /in/ prefix if it exists in the pathname
  return `/investments/news/${newsId}${queryParam}`;
};

import { newsStore } from "../store/newsStore";

/**
 * Hook to navigate to news detail page
 */
export const useNewsNavigation = () => {
  const router = useRouter();
  const pathname = usePathname();

  const navigateToNewsDetail = (newsId: string, newsType?: string) => {
    // Save scroll position before navigation
    newsStore.saveScrollPosition();
    
    const path = getNewsDetailPath(newsId, pathname, newsType);
    router.push(path);
  };

  return { navigateToNewsDetail };
};

